import { ReactNode } from "react";

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";

import { RecordingViewModel } from "../../models/recordingViewModel";
import ExpandableEventDetails from "./ExpandableEventDetails";

export type EventTableColumn<T> = {
  title: ReactNode;
  width?: string;
  content: (item: T) => ReactNode;
};

export type ExpandableData = { label: string; value: ReactNode };

export type EventRecords = {
  url: string;
  timestamp?: string;
  rateMCF?: string;
};

interface EventTableProps<T extends { id: string }> {
  columns: EventTableColumn<T>[];
  event: T;
  expandableData: ExpandableData[];
  records?: RecordingViewModel[];
  latestRecordingTimestamp?: string;
  fetchNextPage: () => Promise<void>;
  shouldLoadMore?: boolean;
}

const EventTable = <T,>({
  columns,
  event,
  expandableData,
  records = [],
  latestRecordingTimestamp,
  fetchNextPage,
  shouldLoadMore,
}: EventTableProps<T & { id: string }>) => {
  const theme = useTheme();

  return (
    <TableContainer
      component={Box}
      sx={{
        borderRadius: "0.625rem",
        overflow: "hidden",
        border: `1px solid ${theme.palette.otherOutlineBorder.main}`,
      }}
    >
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((column, idx) => (
              <TableCell
                key={idx}
                sx={{
                  backgroundColor: theme.palette.otherOutlineBorder.main,
                  textTransform: "uppercase",
                  width: column.width,
                  padding: "12px 16px",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  {column.title}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {columns.map((column, idx) => (
              <TableCell
                key={String(column.title) + idx}
                sx={{
                  padding: "12px 16px",
                  backgroundColor: theme.palette.otherBackgroundLight.main,
                  borderBottom: `1px solid ${theme.palette.otherOutlineBorder.main}`,
                }}
              >
                {column.content(event)}
              </TableCell>
            ))}
          </TableRow>
          <ExpandableEventDetails
            colSpan={columns.length}
            records={records}
            expandableData={expandableData}
            latestRecordingTimestamp={latestRecordingTimestamp}
            fetchNextPage={fetchNextPage}
            shouldLoadMore={shouldLoadMore}
          />
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default EventTable;
